import { FC, ReactNode, useCallback } from "react";

import { Shelf } from "capitalrx-components-2.0/lib/layout";

import { Button, ButtonAppearance, Buttons } from "../Button";
import { ApproveModalCloseEventHandler } from "./ApproveModalCloseEventHandler";
import { Modal, ModalProps } from "./Modal";

/**
 * The `ApproveModal` component properties.
 */
export interface ApproveModalProps extends Omit<ModalProps, "onClose" | "footer"> {
  /**
   * The content within the approval button. If not specified defaults to "Yes".
   */
  readonly approveContent?: ReactNode;

  /**
   * The button appearence type for the primary "appove" button. Defaults to "Functional"
   */
  readonly approveButtonAppearance?: ButtonAppearance;

  /**
   * The content within the disapproval button. If not specified defaults to "No".
   */
  readonly denyContent?: ReactNode;

  /**
   * Called when the modal should be closed.
   */
  readonly onClose: ApproveModalCloseEventHandler;
}

/**
 * A component which displays a Modal dialog box.
 */
export const ApproveModal: FC<ApproveModalProps> = (props) => {
  const {
    title,
    visible,
    approveContent = "Yes",
    approveButtonAppearance = Buttons.Functional.Primary.Large,
    denyContent = "No",
    onClose,
    children,
    testId,
    size,
    id,
  } = props;

  const denyButtonTestId = testId ? `${testId}-deny-btn` : undefined;
  const approveButtonTestId = testId ? `${testId}-approve-btn` : undefined;

  const approveHandler = useCallback(() => {
    onClose(true);
  }, [onClose]);

  const denyHandler = useCallback(() => {
    onClose(false);
  }, [onClose]);

  return (
    <Modal
      id={id}
      title={title}
      visible={visible}
      onClose={denyHandler}
      testId={testId}
      size={size}
      footer={
        <Shelf spacing="small">
          <Shelf.Spacer />
          <Button appearance={Buttons.Functional.Secondary.Large} onClick={denyHandler} testId={denyButtonTestId}>
            {denyContent}
          </Button>
          <Button appearance={approveButtonAppearance} onClick={approveHandler} testId={approveButtonTestId} autoFocus>
            {approveContent}
          </Button>
        </Shelf>
      }
    >
      {children}
    </Modal>
  );
};
